import React from 'react';
import { useTranslation } from 'react-i18next';

export function TooltipSpan({ 
  text, 
  tooltip, 
  textClassName = "text-emerald-400", 
  positionClassName = "left-0 sm:left-1/2 sm:-translate-x-1/2",
  placement = "bottom",
  containerClassName = "",
  isFocusable = true,
  showUnderline = true
}: { 
  text: React.ReactNode; 
  tooltip: string; 
  textClassName?: string; 
  positionClassName?: string; 
  placement?: "top" | "bottom";
  containerClassName?: string;
  isFocusable?: boolean;
  /** Dashed underline affordance. Turn off when the trigger already reads as interactive
   *  on its own - a pill badge, for instance, where an underline just looks broken. */
  showUnderline?: boolean;
  key?: React.Key 
}) {
  const placementClasses = placement === "top" ? "bottom-full mb-2" : "top-full mt-2";
  const isFlex = containerClassName.includes('flex') || containerClassName.includes('inline-flex');
  const displayClass = isFlex || containerClassName.includes('block') || containerClassName.includes('inline-block') ? '' : 'inline-block';
  const marginClass = containerClassName.includes('mx-') || containerClassName.includes('m-') ? '' : 'mx-0.5';

  return (
    <span 
      className={`relative ${displayClass} cursor-help group ${marginClass} ${showUnderline ? 'border-b border-dashed border-zinc-400' : ''} ${containerClassName}`}
      {...(isFocusable ? { tabIndex: 0 } : {})}
    >
      <span className={textClassName ? textClassName : isFlex ? 'inline-flex items-center leading-none' : undefined}>{text}</span>
      <span className={`absolute z-50 w-max max-w-[200px] sm:max-w-[280px] p-2 text-[10px] sm:text-[11px] leading-relaxed text-zinc-100 bg-zinc-900 border border-zinc-700 rounded-sm shadow-2xl text-left font-sans pointer-events-none break-words whitespace-pre-line ${positionClassName} ${placementClasses} opacity-0 invisible group-hover:opacity-100 group-hover:visible ${isFocusable ? 'group-focus:opacity-100 group-focus:visible' : ''} transition-all duration-200`}>
        {tooltip}
      </span>
    </span>
  );
}

export function TextWithTooltips({ text }: { text: string }) {
  const { t } = useTranslation();
  const dictionary = [
    {
      term: t('tooltips.capped_location_filter.term'),
      replacement: t('tooltips.capped_location_filter.term'),
      tooltip: t('tooltips.capped_location_filter.tooltip')
    },
    {
      term: t('tooltips.location_capped.term'),
      replacement: t('tooltips.location_capped.term'),
      tooltip: t('tooltips.location_capped.tooltip')
    }
  ];

  let result: React.ReactNode[] = [text];

  dictionary.forEach(({ term, replacement, tooltip }) => {
    const newResult: React.ReactNode[] = [];
    result.forEach((chunk) => {
      if (typeof chunk === 'string') {
        const parts = chunk.split(term);
        parts.forEach((part, index) => {
          newResult.push(part);
          if (index < parts.length - 1) {
            newResult.push(<TooltipSpan key={`${term}-${index}`} text={replacement} tooltip={tooltip} />);
          }
        });
      } else {
        newResult.push(chunk);
      }
    });
    result = newResult;
  });

  return <>{result.map((r, i) => <React.Fragment key={i}>{r}</React.Fragment>)}</>;
}
